import type { OfferDraftRowKind } from '../../../types';
import { contributesToOfferNetTotal, lineTotalCents } from './offerEventualPosition';
import { applyPositionSurchargePricing } from './positionSurcharge';
import { clampTitleLevel, MIN_TITLE_LEVEL } from './offerTitleHierarchy';

export const VIRTUAL_TITLE_LABOR_ID_PREFIX = 'virtual-title-labor-';

export const DEFAULT_TITLE_LABOR_ROW_LABEL = 'Arbeitszeit';

export type TitleLaborRowInjectOptions = {
  laborRowLabel?: string;
  laborRowNames?: Record<string, string>;
};

export type CustomerLaborPricingOptions = {
  hourlyRate: number;
  listLaborSeparately?: boolean;
  laborRowLabel?: string;
  laborRowNames?: Record<string, string>;
};

export type LaborPricedRow = {
  id: string;
  kind: OfferDraftRowKind;
  quantity: number;
  unit?: string;
  item_name?: string;
  unit_price_vk: number;
  labor_minutes?: number;
  hourly_rate?: number | null;
  isEventual?: boolean | null;
  costKind?: 'neutral' | 'leistung' | 'material';
  sourceType?: string | null;
  positionSurcharge?: { percent: number; targetRowIds: string[] } | null;
  titleLevel?: number | null;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function normalizeHourlyRate(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/** Positions-Override: gesetzt und > 0, sonst Default erben. 0/leer wird zu null. */
export function parseHourlyRateOverride(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Draft-Zahl gewinnt; null/fehlend übernimmt den API-Wert (Alt-Drafts ohne Feld). */
export function mergeHourlyRateOverride(draftRate: unknown, apiRate: unknown): number | null {
  return parseHourlyRateOverride(draftRate) ?? parseHourlyRateOverride(apiRate);
}

export function effectiveHourlyRate(
  row: { hourly_rate?: number | null },
  defaultRate: number,
): number {
  const override = parseHourlyRateOverride(row.hourly_rate);
  return override ?? normalizeHourlyRate(defaultRate);
}

/**
 * Dokument-EP einer Stundenzeile → Positions-Override.
 * 0/leer erbt den Angebots-Default. Gleicher Default ohne bestehenden Override
 * bleibt erben (Fokus/Tab legt keinen Override an).
 */
export function hourlyRateOverrideFromEpInput(
  committed: number,
  currentOverride: number | null,
  defaultRate: number,
): number | null {
  const next = parseHourlyRateOverride(committed);
  if (next == null) return null;
  const current = parseHourlyRateOverride(currentOverride);
  if (current == null && next === normalizeHourlyRate(defaultRate)) return null;
  return next;
}

export const MINUTES_PER_LABOR_HOUR = 60;

const HOUR_LABOR_UNITS = new Set(['h', 'Lh']);

function storedMinutes(laborMinutes?: number | null): number {
  const minutes = laborMinutes ?? 0;
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return minutes;
}

/** Einheit `h` / `Lh`: reine Lohnposition, keine Materialkalkulation. */
export function isHourLaborUnit(unit: string | null | undefined): boolean {
  return HOUR_LABOR_UNITS.has(String(unit ?? '').trim());
}

/**
 * Neue Lohn-Stundenzeile: Einheit Stunde und gespeicherte Minuten > 0.
 * Altbestand (`h` + Preis im VK + Minuten 0) bleibt beim gespeicherten VK.
 */
export function isHourLaborPricedRow(row: {
  unit?: string | null;
  labor_minutes?: number | null;
}): boolean {
  return isHourLaborUnit(row.unit) && storedMinutes(row.labor_minutes) > 0;
}

export type HourLaborUnitFields = {
  labor_minutes: number;
  unit_cost_ek: number;
  surcharge_percent: number;
  unit_price_vk: number;
};

export function hourLaborUnitFields(): HourLaborUnitFields {
  return {
    labor_minutes: MINUTES_PER_LABOR_HOUR,
    unit_cost_ek: 0,
    surcharge_percent: 0,
    unit_price_vk: 0,
  };
}

export function patchForUnitChange(
  unit: string,
  previousUnit?: string | null,
): { unit: string } & Partial<HourLaborUnitFields> {
  if (!isHourLaborUnit(unit)) return { unit };
  if (previousUnit != null && isHourLaborUnit(previousUnit)) return { unit };
  return { unit, ...hourLaborUnitFields() };
}

export function applyHourLaborPatchIfNeeded<P extends object>(
  row: { unit?: string | null; labor_minutes?: number | null },
  patch: P,
): P & Partial<HourLaborUnitFields> {
  const unitFromPatch =
    'unit' in patch && typeof (patch as { unit?: unknown }).unit === 'string'
      ? (patch as { unit: string }).unit
      : undefined;
  const nextUnit = unitFromPatch ?? row.unit ?? '';
  const switchingToHour =
    unitFromPatch != null && isHourLaborUnit(unitFromPatch) && !isHourLaborUnit(row.unit);
  const staysHourLabor = isHourLaborPricedRow(row) && isHourLaborUnit(nextUnit);
  if (!switchingToHour && !staysHourLabor) return patch;
  return { ...patch, ...hourLaborUnitFields() };
}

/** Minuten je Einheit: bei neuen Stundenzeilen 60, Altbestand 0, sonst gespeichert. */
export function effectiveLaborMinutes(row: {
  unit?: string | null;
  labor_minutes?: number | null;
}): number {
  if (isHourLaborUnit(row.unit)) {
    return storedMinutes(row.labor_minutes) > 0 ? MINUTES_PER_LABOR_HOUR : 0;
  }
  return storedMinutes(row.labor_minutes);
}

export function storedMaterialVk(row: {
  unit?: string | null;
  labor_minutes?: number | null;
  unit_price_vk: number;
}): number {
  if (isHourLaborPricedRow(row)) return 0;
  return Number.isFinite(row.unit_price_vk) ? row.unit_price_vk : 0;
}

export function laborHoursFromMinutes(laborMinutes: number): number {
  if (!Number.isFinite(laborMinutes) || laborMinutes <= 0) return 0;
  return laborMinutes / MINUTES_PER_LABOR_HOUR;
}

/** Lohnanteil einer Einheit: Minuten/60 × Stundensatz. 0 wenn Satz oder Minuten fehlen. */
export function laborSharePerUnit(laborMinutes: number, hourlyRate: number): number {
  const rate = normalizeHourlyRate(hourlyRate);
  const hours = laborHoursFromMinutes(laborMinutes);
  if (rate <= 0 || hours <= 0) return 0;
  return round2(hours * rate);
}

/** Kunden-EP (Modus A): Material-VK + Lohnanteil. Gespeicherter Material-VK bleibt unverändert. */
export function effectiveUnitPriceVk(
  materialVk: number,
  laborMinutes: number,
  hourlyRate: number,
): number {
  const vk = Number.isFinite(materialVk) ? materialVk : 0;
  return round2(vk + laborSharePerUnit(laborMinutes, hourlyRate));
}

export function effectiveLineTotal(
  quantity: number,
  materialVk: number,
  laborMinutes: number,
  hourlyRate: number,
): number {
  return lineTotalCents(quantity, effectiveUnitPriceVk(materialVk, laborMinutes, hourlyRate));
}

export function isPricedLaborTargetKind(kind: OfferDraftRowKind): boolean {
  return kind === 'apiPosition' || kind === 'freePosition';
}

export function isVirtualTitleLaborRow(row: { id?: string }): boolean {
  return typeof row.id === 'string' && row.id.startsWith(VIRTUAL_TITLE_LABOR_ID_PREFIX);
}

export function virtualTitleLaborParentId(rowId: string): string | null {
  if (!isVirtualTitleLaborRow({ id: rowId })) return null;
  const parentId = rowId.slice(VIRTUAL_TITLE_LABOR_ID_PREFIX.length);
  return parentId || null;
}

export function titleLaborRowLabelUsesPlaceholders(label: unknown): boolean {
  const value = typeof label === 'string' ? label : '';
  return value.includes('{hours}') || value.includes('{rate}');
}

/** Insert-Index in der sichtbaren Tabelle → Index ohne virtuelle Arbeitszeitzeilen. */
export function countNonVirtualRowsBefore(
  rows: Array<{ id?: string }>,
  index: number,
): number {
  const end = Math.max(0, Math.min(rows.length, index));
  let count = 0;
  for (let i = 0; i < end; i += 1) {
    if (!isVirtualTitleLaborRow(rows[i]!)) count += 1;
  }
  return count;
}

export function normalizeTitleLaborRowLabel(value: unknown): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed || DEFAULT_TITLE_LABOR_ROW_LABEL;
}

export function formatTitleLaborRowSuffix(hours: number, hourlyRate: number): string {
  const hoursLabel = hours.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const rateLabel = hourlyRate.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${hoursLabel} Std à ${rateLabel} €`;
}

export function formatTitleLaborRowName(
  hours: number,
  hourlyRate: number,
  label: unknown = DEFAULT_TITLE_LABOR_ROW_LABEL,
): string {
  const suffix = formatTitleLaborRowSuffix(hours, hourlyRate);
  const trimmed = normalizeTitleLaborRowLabel(label);
  if (trimmed.includes('{hours}') || trimmed.includes('{rate}')) {
    const hoursLabel = hours.toLocaleString('de-DE', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const rateLabel = hourlyRate.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return trimmed.replaceAll('{hours}', hoursLabel).replaceAll('{rate}', rateLabel);
  }
  return `${trimmed} ${suffix}`;
}

export const TITLE_LABOR_ROW_NAME_MAX = 200;

export function normalizeTitleLaborRowNames(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!key || typeof raw !== 'string') continue;
    const trimmed = raw.trim().slice(0, TITLE_LABOR_ROW_NAME_MAX);
    if (trimmed) out[key] = trimmed;
  }
  return out;
}

/** Editierten Zeilentext speichern: aktuelle Stunden/Satz werden zu Platzhaltern. */
export function storeTitleLaborRowName(
  edited: unknown,
  hours: number,
  hourlyRate: number,
): string {
  const trimmed = typeof edited === 'string' ? edited.trim().slice(0, TITLE_LABOR_ROW_NAME_MAX) : '';
  if (!trimmed) return '';
  const hoursLabel = hours.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const rateLabel = hourlyRate.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return trimmed.replaceAll(hoursLabel, '{hours}').replaceAll(rateLabel, '{rate}');
}

export function resolveTitleLaborRowName(
  hours: number,
  hourlyRate: number,
  stored: unknown,
  fallbackLabel?: unknown,
): string {
  const custom = typeof stored === 'string' ? stored.trim() : '';
  if (!custom) return formatTitleLaborRowName(hours, hourlyRate, fallbackLabel);
  if (titleLaborRowLabelUsesPlaceholders(custom)) {
    return formatTitleLaborRowName(hours, hourlyRate, custom);
  }
  return custom.slice(0, TITLE_LABOR_ROW_NAME_MAX);
}

function laborLineShare(
  quantity: number,
  laborMinutes: number,
  hourlyRate: number,
): number {
  const hours = quantity * laborHoursFromMinutes(laborMinutes);
  return lineTotalCents(hours, normalizeHourlyRate(hourlyRate));
}

/**
 * Modus B: nach dem letzten Kind jedes Titels eine berechnete Lohnzeile.
 * Summe der Zeilen entspricht Modus A (Lohnanteil aus den Material-EPs).
 */
export function injectTitleLaborRows<T extends LaborPricedRow>(
  rows: T[],
  hourlyRate: number,
  opts?: string | TitleLaborRowInjectOptions,
): T[] {
  const laborRowLabel = typeof opts === 'string' ? opts : opts?.laborRowLabel;
  const laborRowNames = typeof opts === 'string' ? undefined : opts?.laborRowNames;
  const rate = normalizeHourlyRate(hourlyRate);

  const out: T[] = [];
  type OpenTitle = { row: T | null; started: boolean; laborHours: number; laborTotal: number; template: T | null };
  const stack: OpenTitle[] = [{ row: null, started: false, laborHours: 0, laborTotal: 0, template: null }];

  const flush = (entry: OpenTitle) => {
    if (entry.laborHours > 0 && entry.laborTotal !== 0 && entry.template) {
      const hours = round2(entry.laborHours);
      const unitPrice = hours > 0 ? round2(entry.laborTotal / hours) : rate;
      const parentId = entry.row && entry.started ? entry.row.id : 'root';
      out.push({
        ...entry.template,
        id: `${VIRTUAL_TITLE_LABOR_ID_PREFIX}${parentId}`,
        kind: 'freePosition',
        quantity: hours,
        unit: 'Std',
        item_name: resolveTitleLaborRowName(hours, rate, laborRowNames?.[parentId], laborRowLabel),
        description: '',
        unit_price_vk: unitPrice,
        unit_cost_ek: 0,
        surcharge_percent: 0,
        labor_minutes: 0,
        hourly_rate: null,
        isEventual: false,
        costKind: 'leistung',
        isApi: false,
        leadPositionId: null,
        image: null,
        components: [],
      } as T);
    }
    entry.laborHours = 0;
    entry.laborTotal = 0;
    entry.template = null;
  };

  for (const row of rows) {
    if (row.kind === 'title') {
      const level = clampTitleLevel(row.titleLevel ?? MIN_TITLE_LEVEL);
      while (stack.length > level) {
        const closed = stack.pop();
        if (closed) flush(closed);
      }
      if (stack.length === level) {
        const closed = stack.pop();
        if (closed) flush(closed);
      }
      stack.push({ row, started: true, laborHours: 0, laborTotal: 0, template: null });
      out.push(row);
      continue;
    }
    out.push(row);
    if (!isPricedLaborTargetKind(row.kind)) continue;
    if (!contributesToOfferNetTotal(row)) continue;
    const minutes = effectiveLaborMinutes(row);
    const hours = row.quantity * laborHoursFromMinutes(minutes);
    if (hours <= 0) continue;
    const current = stack[stack.length - 1];
    if (!current) continue;
    current.template = row;
    current.laborHours += hours;
    current.laborTotal += laborLineShare(row.quantity, minutes, effectiveHourlyRate(row, rate));
  }
  while (stack.length) {
    const closed = stack.pop();
    if (closed) flush(closed);
  }
  return out;
}

/**
 * Kundenpreise nach dem Merge:
 * – Zuschläge immer auf Modus-A-EPs (Material + Lohn), damit A und B dieselbe Summe haben
 * – Modus A: Lohn im Positions-EP
 * – Modus B: Material-VK auf der Position, virtuelle Lohnzeile je Titel
 */
export function applyCustomerFacingLaborPricing<T extends LaborPricedRow>(
  rows: T[],
  opts: CustomerLaborPricingOptions,
): T[] {
  const hourlyRate = normalizeHourlyRate(opts.hourlyRate);
  const listSeparately = opts.listLaborSeparately === true;
  const laborRowLabel = opts.laborRowLabel;
  const laborRowNames = opts.laborRowNames;

  const withModeA = rows.map((row) => {
    if (!isPricedLaborTargetKind(row.kind)) return row;
    return {
      ...row,
      unit_price_vk: effectiveUnitPriceVk(
        storedMaterialVk(row),
        effectiveLaborMinutes(row),
        effectiveHourlyRate(row, hourlyRate),
      ),
    };
  });
  const withSurcharge = applyPositionSurchargePricing(withModeA);
  if (!listSeparately) return withSurcharge;

  const restored = withSurcharge.map((row, index) => {
    if (row.kind === 'positionSurcharge') return row;
    const orig = rows[index];
    if (!orig) return row;
    return { ...row, unit_price_vk: storedMaterialVk(orig) };
  });
  return injectTitleLaborRows(restored, hourlyRate, { laborRowLabel, laborRowNames });
}

/** Lohnanteil einer Zeile, der in Modus A im EP steckt (nicht bei Leistungen / virtuellen Zeilen). */
export function bakedLaborShareFor35a(
  row: LaborPricedRow,
  hourlyRate: number,
  listLaborSeparately: boolean,
): number {
  if (listLaborSeparately) return 0;
  if (!isPricedLaborTargetKind(row.kind) || isVirtualTitleLaborRow(row)) return 0;
  if (!contributesToOfferNetTotal(row)) return 0;
  const source = String(row.sourceType ?? '').trim();
  if (source === 'leistung' || row.costKind === 'leistung') return 0;
  return laborLineShare(
    row.quantity,
    effectiveLaborMinutes(row),
    effectiveHourlyRate(row, hourlyRate),
  );
}
